<template>
  <div class="price-channels-container">
    <!-- PAGE HEADER -->
    <div
      class="ps-page-header d-flex align-center justify-space-between flex-wrap mb-4"
      style="gap: 16px;"
    >
      <div class="d-flex align-center" style="gap: 12px;">
        <span class="ap-card-accent" />
        <div>
          <div class="d-flex align-center" style="gap: 8px;">
            <h1 class="ps-page-title mb-0">
              {{ $t("manage_price_channels") }}
            </h1>
            <v-chip x-small color="primary" outlined class="font-weight-bold">
              {{ channels.length }} {{ $t("items") || "items" }}
            </v-chip>
          </div>
          <div class="ps-page-subtitle">
            {{ $t("price_channel_subtitle") }}
          </div>
        </div>
      </div>

      <div class="d-flex align-center" style="gap: 10px;">
        <v-btn
          icon
          outlined
          small
          class="rounded-lg sync-btn"
          :loading="overlay"
          :title="$t('refresh') || 'Refresh'"
          @click="fetchChannels"
        >
          <v-icon small color="#64748b">fal fa-sync-alt</v-icon>
        </v-btn>

        <v-btn
          depressed
          color="primary"
          class="white--text font-weight-bold text-none rounded-lg px-4"
          @click="newItem"
        >
          <v-icon left small>fal fa-plus-circle</v-icon>
          {{ $t("new_price_channel") }}
        </v-btn>
      </div>
    </div>

    <!-- KPI STRIP -->
    <KpiCards
      class="mb-4"
      :items="kpiItems"
      :loading="overlay && !channels.length"
      :skeleton="3"
    />

    <!-- MAIN CARD -->
    <v-card class="ap-card" outlined elevation="0">
      <!-- Toolbar: Search + Status Filter Tabs + View Mode Toggle -->
      <div
        class="py-3 px-4 border-bottom d-flex align-center justify-space-between flex-wrap"
        style="gap: 12px;"
      >
        <div
          class="d-flex align-center flex-grow-1"
          style="max-width: 420px; min-width: 240px;"
        >
          <TextSearch
            v-model="search"
            :placeholder="$t('search')"
            @search="currentPage = 1"
            @onClear="
              search = '';
              currentPage = 1;
            "
          />
        </div>

        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <!-- Segmented Filter Tabs (.br-tabs) -->
          <div class="br-tabs">
            <div
              v-for="(item, i) in statusFilterList"
              :key="i"
              class="br-tab"
              :class="{ 'br-tab--active': statusFilter === item.value }"
              @click="statusFilter = item.value"
            >
              {{ $t(item.text) }}
            </div>
          </div>

          <!-- View Mode Toggle (Table / Grid) -->
          <div class="view-mode-toggle d-flex align-center">
            <button
              class="view-mode-btn"
              :class="{ 'view-mode-btn--active': viewMode === 'table' }"
              :title="$t('table_view') || 'Table View'"
              @click="viewMode = 'table'"
            >
              <v-icon small>fal fa-list</v-icon>
            </button>
            <button
              class="view-mode-btn"
              :class="{ 'view-mode-btn--active': viewMode === 'grid' }"
              :title="$t('card_view') || 'Card View'"
              @click="viewMode = 'grid'"
            >
              <v-icon small>fal fa-th-large</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="price-channels-table-container">
        <v-data-table
          :headers="headers"
          :items="filteredChannels"
          :loading="overlay"
          hide-default-footer
          class="price-channels-table elevation-0"
          :items-per-page="50"
        >
          <!-- Row index -->
          <template slot="item.index" slot-scope="{ index }">
            <span class="ps-row-index">{{ index + 1 }}</span>
          </template>

          <!-- Channel Info (Icon + Name + ID) -->
          <template slot="item.name" slot-scope="{ item }">
            <div
              class="d-flex align-center py-2"
              style="cursor: pointer;"
              @click="editItem(item)"
            >
              <div class="channel-thumb-box mr-3">
                <app-img
                  v-if="item.icon"
                  :img="item.icon"
                  width="38"
                  height="38"
                />
                <v-icon v-else size="20" color="#64748b">
                  fal fa-tags
                </v-icon>
              </div>
              <div class="min-w-0">
                <div class="ps-channel-name text-truncate" :title="item.name">
                  {{ item.name }}
                </div>
                <div class="d-flex align-center mt-1" style="gap: 6px;">
                  <span class="ps-channel-code">#{{ item.id }}</span>
                  <span
                    v-if="item.is_active"
                    class="text-caption text-success d-none d-sm-inline"
                  >
                    • {{ $t("active") }}
                  </span>
                </div>
              </div>
            </div>
          </template>

          <!-- Status + Quick Inline Toggle -->
          <template slot="item.status" slot-scope="{ item }">
            <div class="d-flex align-center" style="gap: 12px;">
              <span
                class="stock-pill"
                :class="item.is_active ? 'stock-pill-normal' : 'stock-pill-out'"
              >
                {{ item.is_active ? $t("active") : $t("inactive") }}
              </span>
              <v-switch
                :input-value="item.is_active"
                :loading="togglingId === item.id"
                :disabled="togglingId === item.id"
                color="success"
                class="mt-0 pt-0 inline-status-switch"
                hide-details
                inset
                @change="toggleStatus(item, $event)"
              />
            </div>
          </template>

          <!-- Actions -->
          <template slot="item.actions" slot-scope="{ item }">
            <div class="d-flex align-center justify-end" style="gap: 8px;">
              <button
                class="ps-act"
                :title="$t('edit')"
                @click="editItem(item)"
              >
                <v-icon small>fal fa-pen</v-icon>
              </button>
              <button
                class="ps-act ps-act--danger"
                :title="$t('delete')"
                @click="deleteItem(item)"
              >
                <v-icon small>fal fa-trash-alt</v-icon>
              </button>
            </div>
          </template>

          <!-- Empty State -->
          <template slot="no-data">
            <div class="ps-empty py-12 text-center">
              <div class="ps-empty__icon-wrap mb-3">
                <v-icon size="44" color="#94a3b8">fal fa-tags</v-icon>
              </div>
              <div class="ps-empty__title mb-1">
                {{ $t("no_price_channels_found") }}
              </div>
              <div class="ps-empty__desc mb-4 grey--text text-caption">
                {{ $t("create_first_price_channel") }}
              </div>
              <v-btn
                depressed
                color="primary"
                class="rounded-lg text-none px-4 font-weight-bold"
                @click="newItem"
              >
                <v-icon left small>fal fa-plus-circle</v-icon>
                {{ $t("new_price_channel") }}
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- GRID / CARD VIEW -->
      <div v-else class="pa-4">
        <v-row v-if="filteredChannels.length">
          <v-col
            v-for="channel in filteredChannels"
            :key="channel.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <div class="channel-card">
              <!-- Top row: Avatar + Status + Switch -->
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="channel-thumb-box">
                  <app-img
                    v-if="channel.icon"
                    :img="channel.icon"
                    width="44"
                    height="44"
                  />
                  <v-icon v-else size="22" color="#64748b">
                    fal fa-tags
                  </v-icon>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                  <span
                    class="stock-pill"
                    :class="channel.is_active ? 'stock-pill-normal' : 'stock-pill-out'"
                  >
                    {{ channel.is_active ? $t("active") : $t("inactive") }}
                  </span>
                  <v-switch
                    :input-value="channel.is_active"
                    :loading="togglingId === channel.id"
                    :disabled="togglingId === channel.id"
                    color="success"
                    class="mt-0 pt-0 inline-status-switch"
                    hide-details
                    inset
                    @change="toggleStatus(channel, $event)"
                  />
                </div>
              </div>

              <!-- Channel Info -->
              <div class="channel-card-body mb-3">
                <div class="ps-channel-name text-truncate" :title="channel.name">
                  {{ channel.name }}
                </div>
                <div class="d-flex align-center mt-1" style="gap: 6px;">
                  <span class="ps-channel-code">#{{ channel.id }}</span>
                </div>
              </div>

              <!-- Bottom Actions -->
              <div
                class="channel-card-footer pt-3 border-top d-flex align-center justify-space-between"
              >
                <button
                  class="channel-card-btn channel-card-btn--edit"
                  @click="editItem(channel)"
                >
                  <v-icon small left>fal fa-pen</v-icon>
                  {{ $t("edit") || "Edit" }}
                </button>
                <button
                  class="channel-card-btn channel-card-btn--danger"
                  @click="deleteItem(channel)"
                >
                  <v-icon small left>fal fa-trash-alt</v-icon>
                  {{ $t("delete") || "Delete" }}
                </button>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Grid Empty State -->
        <div v-else class="ps-empty py-12 text-center">
          <div class="ps-empty__icon-wrap mb-3">
            <v-icon size="44" color="#94a3b8">fal fa-tags</v-icon>
          </div>
          <div class="ps-empty__title mb-1">
            {{ $t("no_price_channels_found") }}
          </div>
          <div class="ps-empty__desc mb-4 grey--text text-caption">
            {{ $t("create_first_price_channel") }}
          </div>
          <v-btn
            depressed
            color="primary"
            class="rounded-lg text-none px-4 font-weight-bold"
            @click="newItem"
          >
            <v-icon left small>fal fa-plus-circle</v-icon>
            {{ $t("new_price_channel") }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- CREATE / EDIT DIALOG -->
    <app-custom-dialog
      v-model="dialog"
      :footer="true"
      :title="update ? 'edit_price_channel' : 'new_price_channel'"
      @submit="submitForm"
    >
      <div slot="activator" class="pa-1">
        <!-- Quick Preset Chips (Only shown on create or when needed) -->
        <div class="mb-4">
          <div class="text-caption font-weight-bold grey--text mb-2 d-flex align-center">
            <v-icon x-small color="#64748b" class="mr-1">fal fa-magic</v-icon>
            {{ $t("popular_presets") }}
          </div>
          <div class="d-flex flex-wrap" style="gap: 6px;">
            <v-chip
              v-for="preset in presets"
              :key="preset.val"
              x-small
              outlined
              class="preset-chip"
              :class="{ 'preset-chip--active': form.name === preset.val }"
              @click="applyPreset(preset.val)"
            >
              {{ preset.label }}
            </v-chip>
          </div>
        </div>

        <!-- Channel Name -->
        <TTextField
          v-model="form.name"
          label="channel_name"
          :error-messages="nameError"
          class="mb-3"
        />

        <!-- Icon Upload -->
        <div class="mb-4">
          <div class="text-caption font-weight-bold grey--text mb-2">
            {{ $t("icon") }} ({{ $t("optional") || "Optional" }})
          </div>
          <DragDropUpload
            v-model="iconFile"
            accept=".svg,image/svg+xml,image/png,image/jpeg"
            :max-size="2 * 1024 * 1024"
            :max-size-m-b="2"
          />
          <div class="text-caption grey--text mt-1">
            SVG, PNG, JPG (max 2MB)
          </div>
        </div>

        <!-- Active Status Switch -->
        <div class="status-switch-box pa-3 rounded-lg border">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="font-weight-bold text-body-2">
                {{ $t("active") }}
              </div>
              <div class="text-caption grey--text">
                {{ form.is_active ? $t("channel_status_updated") : $t("inactive") }}
              </div>
            </div>
            <v-switch
              v-model="form.is_active"
              color="primary"
              class="mt-0 pt-0"
              hide-details
              inset
            />
          </div>
        </div>
      </div>
    </app-custom-dialog>

    <app-loading :overlay="overlay" />
  </div>
</template>

<script>
import TTextField from "@/components/global/TextField";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import { required } from "vuelidate/lib/validators";
import { success, confirm, errorAlert } from "@/controllers/alert";

export default {
  name: "PriceChannels",
  components: {
    TTextField,
    DragDropUpload,
  },
  data() {
    return {
      overlay: false,
      dialog: false,
      update: false,
      channels: [],
      iconFile: null,
      attemptedSubmit: false,
      search: "",
      statusFilter: "all",
      viewMode: "table",
      togglingId: null,
      form: {
        id: null,
        name: "",
        is_active: true,
      },
      statusFilterList: [
        { text: "all", value: "all" },
        { text: "active", value: "active" },
        { text: "inactive", value: "inactive" },
      ],
      presets: [
        { label: "หน้าร้าน / Dine-in", val: "หน้าร้าน (Dine-in)" },
        { label: "สั่งกลับบ้าน / Takeaway", val: "สั่งกลับบ้าน (Takeaway)" },
        { label: "GrabFood", val: "GrabFood" },
        { label: "FoodPanda", val: "FoodPanda" },
        { label: "ShopeeFood", val: "ShopeeFood" },
        { label: "LINE MAN", val: "LINE MAN" },
        { label: "Delivery", val: "Delivery" },
        { label: "ขายส่ง / Wholesale", val: "ขายส่ง (Wholesale)" },
        { label: "VIP / Member", val: "VIP" },
      ],
    };
  },
  validations: {
    form: {
      name: { required },
    },
  },
  computed: {
    headers() {
      return [
        { text: "#", value: "index", width: "55px", sortable: false },
        { text: this.$t("channel_name") || "Channel", value: "name", sortable: true },
        { text: this.$t("status") || "Status", value: "status", width: "180px", sortable: false },
        { text: this.$t("actions") || "Actions", value: "actions", width: "110px", align: "right", sortable: false },
      ];
    },
    activeChannelsCount() {
      return this.channels.filter((c) => Boolean(c.is_active)).length;
    },
    inactiveChannelsCount() {
      return this.channels.filter((c) => !c.is_active).length;
    },
    kpiItems() {
      return [
        {
          key: "total",
          label: this.$t("total_channels") || "Total Channels",
          icon: "fal fa-tags",
          iconColor: "primary",
          tone: "primary",
          value: this.channels.length,
        },
        {
          key: "active",
          label: this.$t("active_channels") || "Active Channels",
          icon: "fal fa-check-circle",
          iconColor: "success",
          tone: "success",
          value: this.activeChannelsCount,
        },
        {
          key: "inactive",
          label: this.$t("inactive_channels") || "Inactive Channels",
          icon: "fal fa-ban",
          iconColor: "error",
          tone: "danger",
          value: this.inactiveChannelsCount,
        },
      ];
    },
    filteredChannels() {
      const q = (this.search || "").trim().toLowerCase();
      return this.channels.filter((channel) => {
        // Status filter
        if (this.statusFilter === "active" && !channel.is_active) {
          return false;
        }
        if (this.statusFilter === "inactive" && channel.is_active) {
          return false;
        }
        // Search query
        if (q && !channel.name?.toLowerCase().includes(q)) {
          return false;
        }
        return true;
      });
    },
    nameError() {
      const errors = [];
      if (!this.$v.form.name.$dirty) {
        return errors;
      }
      if (!this.$v.form.name.required) {
        errors.push(this.$t("validation_required"));
      }
      return errors;
    },
  },
  created() {
    this.fetchChannels();
  },
  methods: {
    async fetchChannels() {
      try {
        this.overlay = true;
        const { data } = await this.$http.get("price_channels");
        this.channels = Array.isArray(data?.data) ? data.data : [];
      } catch (error) {
        await errorAlert(this, "Error fetching price channels");
      } finally {
        this.overlay = false;
      }
    },
    applyPreset(name) {
      this.form.name = name;
      this.$v.form.name.$touch();
    },
    newItem() {
      this.update = false;
      this.form = {
        id: null,
        name: "",
        is_active: true,
      };
      this.iconFile = null;
      this.attemptedSubmit = false;
      this.$v.$reset();
      this.dialog = true;
    },
    editItem(channel) {
      this.update = true;
      this.form = {
        id: channel.id,
        name: channel.name || "",
        is_active: Boolean(channel.is_active),
      };
      this.iconFile = channel.icon && window.Help?.img ? window.Help.img(channel.icon) : null;
      this.attemptedSubmit = false;
      this.$v.$reset();
      this.dialog = true;
    },
    async toggleStatus(channel, eventValue) {
      const newStatus = Boolean(eventValue);
      const originalStatus = Boolean(channel.is_active);
      if (newStatus === originalStatus) return;

      // Optimistic update
      channel.is_active = newStatus ? 1 : 0;
      this.togglingId = channel.id;

      try {
        const payload = {
          name: channel.name,
          is_active: newStatus ? 1 : 0,
        };
        const { data } = await this.$http.post(`price_channels_update/${channel.id}`, payload);
        if (data.status) {
          if (this.$toast) {
            this.$toast.success(this.$t("channel_status_updated"));
          }
        } else {
          // Revert on server error
          channel.is_active = originalStatus ? 1 : 0;
          await errorAlert(this, data.message || "Failed to update channel status");
        }
      } catch (error) {
        channel.is_active = originalStatus ? 1 : 0;
        await errorAlert(this, "Error updating channel status");
      } finally {
        this.togglingId = null;
      }
    },
    async submitForm() {
      this.attemptedSubmit = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        this.overlay = true;
        const fields = {
          name: this.form.name,
          is_active: this.form.is_active ? 1 : 0,
        };
        const payload = this.buildPayload(fields);
        const config = payload instanceof FormData
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : undefined;
        const url = this.update
          ? `price_channels_update/${this.form.id}`
          : "price_channels";
        const { data } = await this.$http.post(url, payload, config);

        if (data.status) {
          this.dialog = false;
          this.iconFile = null;
          await this.fetchChannels();
          await success(this, data.message);
        } else {
          await errorAlert(this, data.message);
        }
      } catch (error) {
        await errorAlert(this, "Error saving price channel");
      } finally {
        this.overlay = false;
      }
    },
    buildPayload(fields) {
      if (!(this.iconFile instanceof File)) {
        return fields;
      }

      const formData = new FormData();
      Object.keys(fields).forEach((key) => {
        if (fields[key] !== undefined && fields[key] !== null) {
          formData.append(key, fields[key]);
        }
      });
      formData.append("icon", this.iconFile);
      return formData;
    },
    async deleteItem(channel) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete_price_channel",
      });

      if (!isConfirmed) {
        return;
      }

      try {
        this.overlay = true;
        const { data } = await this.$http.delete(`price_channels/${channel.id}`);
        if (data.status) {
          await this.fetchChannels();
          await success(this, data.message);
        } else {
          await errorAlert(this, data.message);
        }
      } catch (error) {
        await errorAlert(this, "Error deleting price channel");
      } finally {
        this.overlay = false;
      }
    },
  },
};
</script>

<style scoped>
.price-channels-container {
  width: 100%;
}

/* Header */
.ps-page-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.ps-page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.sync-btn {
  border-color: #e2e8f0;
}

/* Accent Bar */
.ap-card-accent {
  display: inline-block;
  width: 4px;
  height: 28px;
  background-color: #5b39c9;
  border-radius: 4px;
}

/* Card */
.ap-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

/* View Mode Toggle */
.view-mode-toggle {
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.view-mode-btn {
  width: 32px;
  height: 30px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.view-mode-btn:hover {
  color: #1e293b;
}

.view-mode-btn--active {
  background: #ffffff;
  color: #5b39c9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

/* Row Index */
.ps-row-index {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
}

/* Channel Avatar Box */
.channel-thumb-box {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

/* Channel Name & ID */
.ps-channel-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}

.ps-channel-code {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
}

/* Stock / Status Pill */
.stock-pill {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
}

.stock-pill-out {
  background: #fef2f2;
  color: #ef4444;
}

.stock-pill-normal {
  background: #f0fdf4;
  color: #16a34a;
}

/* Inline switch */
.inline-status-switch {
  margin: 0 !important;
}

/* Action button */
.ps-act {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

/* Grid Card */
.channel-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.channel-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.channel-card-btn {
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s ease;
}

.channel-card-btn--edit {
  color: #475569;
}

.channel-card-btn--edit:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.channel-card-btn--danger {
  color: #ef4444;
  border-color: #fecaca;
  background: #fff5f5;
}

.channel-card-btn--danger:hover {
  background: #fee2e2;
}

/* Preset Chips */
.preset-chip {
  cursor: pointer;
  border-color: #e2e8f0;
  transition: all 0.15s ease;
}

.preset-chip:hover {
  border-color: #5b39c9;
  color: #5b39c9;
}

.preset-chip--active {
  background-color: rgba(91, 57, 201, 0.08) !important;
  border-color: #5b39c9 !important;
  color: #5b39c9 !important;
  font-weight: 600;
}

/* Status switch box */
.status-switch-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

/* Dark Theme Support */
.theme--dark .ps-page-title {
  color: #f1f5f9;
}

.theme--dark .ps-page-subtitle {
  color: #94a3b8;
}

.theme--dark .ap-card,
.theme--dark .channel-card {
  background: #1e1e2d;
  border-color: #2d2d3f;
}

.theme--dark .channel-thumb-box {
  background: #28283a;
  border-color: #3f3f56;
}

.theme--dark .ps-channel-name {
  color: #f1f5f9;
}

.theme--dark .ps-channel-code {
  background: #28283a;
  color: #94a3b8;
}

.theme--dark .border-bottom,
.theme--dark .border-top {
  border-color: #2d2d3f;
}

.theme--dark .view-mode-toggle {
  background: #28283a;
}

.theme--dark .view-mode-btn--active {
  background: #1e1e2d;
  color: #8b5cf6;
}

.theme--dark .ps-act {
  background: #28283a;
  border-color: #3f3f56;
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #a78bfa;
  border-color: #a78bfa;
  background: rgba(167, 139, 250, 0.1);
}

.theme--dark .ps-act--danger:hover {
  color: #f87171;
  border-color: #f87171;
  background: rgba(248, 113, 113, 0.1);
}

.theme--dark .stock-pill-out {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.theme--dark .stock-pill-normal {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}

.theme--dark .status-switch-box {
  background: #28283a;
  border-color: #3f3f56;
}
</style>
